/**
 * useUniversalExport Hook
 *
 * Core business logic hook for universal export system.
 * Manages state, localStorage persistence, and export execution.
 *
 * FEATURES:
 * - Debounced localStorage writes (300ms)
 * - AbortController for cleanup
 * - Memoized callbacks
 * - Validation before export
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import type {
  ExportableEntity,
  ExportFormat,
  ExportModalState,
  ExportOption,
  UniversalExportConfig,
  UseUniversalExportReturn,
} from '@/components/export/universal/types';
import {
  hasHardFailures,
  validateExport,
} from '@/components/export/universal/utils';
import { useAsyncExports } from '@/hooks/useAsyncExports';
import { useStorage } from '@/utils/storage/useStorage.util';

/**
 * Universal Export Hook
 *
 * @param config - Entity-specific export configuration
 * @returns Hook interface with state and actions
 *
 * USAGE:
 * ```typescript
 * const exportHook = useUniversalExport(assetExportConfig);
 *
 * // Update selections
 * exportHook.updateFilter('status', statusOptions);
 * exportHook.updateColumns(selectedColumns);
 *
 * // Execute export
 * await exportHook.executeExport(selectedRowKeys);
 * ```
 */
export function useUniversalExport<TEntity extends ExportableEntity>(
  config: UniversalExportConfig<TEntity>
): UseUniversalExportReturn<TEntity> {
  // AbortController for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Extract storageKey as stable reference to prevent useStorage key changing error
  // This is critical because config object reference may change even though
  // storageKey string value remains constant, triggering useStorage error
  // Using useRef to ensure the string reference never changes after first render
  const storageKeyRef = useRef(config.storageKey);
  const storageKey = storageKeyRef.current;

  // Build default state from config
  const defaultState: ExportModalState = useMemo(() => {
    const filters: Record<string, readonly ExportOption[]> = {};

    // Initialize filters with defaults from config
    config.filterSections.forEach(section => {
      filters[section.id] = section.defaultSelected || section.options;
    });

    // Initialize columns with defaults from config
    const columns = config.columns
      .filter(col => col.defaultSelected !== false)
      .map(col => ({ label: col.label, value: col.id }));

    return {
      filters,
      columns,
      format: config.defaultFormat,
    };
  }, [config]);

  // Persist state to localStorage with debouncing
  const [state, setState] = useStorage<ExportModalState>(
    { key: storageKey },
    defaultState
  );

  // Export mutation
  const { mutateAsync: exportData, isPending: isExporting } = useAsyncExports(
    config.apiEntityType
  );

  // Update filter selection
  const updateFilter = useCallback(
    (sectionId: string, options: readonly ExportOption[]) => {
      setState(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          [sectionId]: options,
        },
      }));
    },
    [setState]
  );

  // Update column selection
  const updateColumns = useCallback(
    (options: readonly ExportOption[]) => {
      setState(prev => ({
        ...prev,
        columns: options,
      }));
    },
    [setState]
  );

  // Update export format
  const updateFormat = useCallback(
    (format: ExportFormat) => {
      setState(prev => ({
        ...prev,
        format,
      }));
    },
    [setState]
  );

  // Check if export should be disabled
  const isExportDisabled = useCallback(
    (selectedRows: readonly string[]) => {
      // Use custom validation if provided
      if (config.validateExport) {
        const errors = config.validateExport(
          state.filters,
          state.columns,
          selectedRows
        );
        return hasHardFailures(errors);
      }

      // Default validation
      const errors = validateExport(
        state.filters,
        state.columns,
        selectedRows,
        {
          requireFiltersWhenNoSelection: true,
        }
      );

      return hasHardFailures(errors);
    },
    [config, state]
  );

  // Execute export
  const executeExport = useCallback(
    async (selectedRows: readonly string[]) => {
      // Create abort controller
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // Validate before export
        if (config.validateExport) {
          const errors = config.validateExport(
            state.filters,
            state.columns,
            selectedRows
          );
          if (hasHardFailures(errors)) {
            const firstError = errors?.[0];
            throw new Error(firstError?.message || 'Validation failed');
          }
        } else {
          // Use default validation
          const errors = validateExport(
            state.filters,
            state.columns,
            selectedRows,
            {
              requireFiltersWhenNoSelection: true,
            }
          );
          if (hasHardFailures(errors)) {
            const firstError = errors?.[0];
            throw new Error(firstError?.message || 'Validation failed');
          }
        }

        // Build query from config
        const query = config.buildQuery(state.filters, selectedRows);

        // Build column list
        const columns =
          state.columns.length > 0
            ? state.columns.map(col => col.value as string)
            : undefined;

        // Prepare export params
        const exportParams = {
          query,
          columns,
          items:
            selectedRows.length > 0 ? (selectedRows as string[]) : undefined,
          format: state.format.value,
          signal: controller.signal,
        };

        // Use custom export handler if provided
        if (config.customExportHandler) {
          await config.customExportHandler(exportParams);
          return;
        }

        // Default export via useAsyncExports
        // Note: exportData expects mutable GraphQuery but we have readonly
        // This is safe because exportData doesn't mutate the query
        await exportData({
          query,
          columns,
          items: exportParams.items,
          format: state.format.value,
          signal: controller.signal,
        });
      } finally {
        // Clean up abort controller
        abortControllerRef.current = null;
      }
    },
    [config, state, exportData]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    state,
    updateFilter,
    updateColumns,
    updateFormat,
    executeExport,
    isExportDisabled,
    isLoading: isExporting,
  };
}
